import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Store, Plus, Upload, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { MerchantApi } from "@/api";
import { toast } from "sonner";
import { IStore } from "@/interfaces/merchant.interfaces";
import { BUSINESS_TYPE } from "@/constants/merchant.constant";

interface EditStoreDialogProps {
  open: boolean;
  merchantId: string;
  merchant?: any;
  onOpenChange: (open: boolean) => void;
  onUpdateStore: (store: IStore) => void;
  storeToEdit: IStore | null;
}

const optionalZipCodeSchema = Yup.string()
  .transform((value) =>
    typeof value === "string" && value.trim() === "" ? undefined : value?.trim()
  )
  .optional()
  .matches(/^[a-zA-Z0-9]+$/, {
    message: "Zip code can only contain alphanumeric characters",
    excludeEmptyString: true,
  })
  .min(3, {
    message: "Zip code must be at least 3 characters",
    excludeEmptyString: true,
  })
  .max(10, {
    message: "Zip code cannot exceed 10 characters",
    excludeEmptyString: true,
  });

// Unified store form schema
const storeSchema = Yup.object().shape({
  // Common fields
  storeName: Yup.string()
    .required("Store name is required")
    .matches(
      /^[a-zA-Z0-9\s]+$/,
      "Store name cannot contain special characters"
    ),
  address: Yup.string().required("Address is required"),
  zipCode: optionalZipCodeSchema,
  city: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, "City can only contain letters and spaces")
    .required("City is required"),

  // Online store fields
  isEpos: Yup.boolean().optional(),
  latitude: Yup.number()
    .typeError("Latitude must be a number")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90")
    .optional(),
  longitude: Yup.number()
    .typeError("Longitude must be a number")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180")
    .optional(),

  // Offline store fields
  storeImage: Yup.mixed().nullable().optional(),
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d+$/, "Contact number can only contain digits")
    .min(7, "Contact number must be at least 7 digits")
    .max(15, "Contact number cannot exceed 15 digits"),
  storeMapLink: Yup.string()
    .url("Store map link must be a valid URL")
    .optional(),
  emailAddress: Yup.string()
    .email("Email must be a valid email address")
    .required("Email address is required"),
});

export function EditStoreDialog({
  open,
  merchantId,
  merchant,
  onOpenChange,
  onUpdateStore,
  storeToEdit,
}: EditStoreDialogProps) {
  const isOnlineBusiness = merchant?.businessType === BUSINESS_TYPE.ONLINE;
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const form = useForm({
    resolver: yupResolver(storeSchema),
    mode: "onSubmit",
    defaultValues: {
      storeName: "",
      address: "",
      zipCode: "",
      city: "",
      isEpos: false,
      latitude: 24.4467032,
      longitude: 54.6178367,
      // Offline fields
      storeImage: null,
      contactNumber: "",
      storeMapLink: "",
      emailAddress: "",
    },
  });

  // Reset form when storeToEdit changes
  React.useEffect(() => {
    if (storeToEdit && open) {
      console.log("Store to edit data:", storeToEdit);
      const formData = {
        storeName: storeToEdit.storeName || "",
        address: storeToEdit.address || "",
        zipCode: storeToEdit.zipCode || "",
        city: storeToEdit.city || "",
        isEpos: storeToEdit.isEpos || false,
        latitude: parseFloat(storeToEdit.latitude) || 24.4467032,
        longitude: parseFloat(storeToEdit.longitude) || 54.6178367,
        // Keep existing image value so validation passes when no new file is chosen
        storeImage: storeToEdit.storeImageUri || null,
        contactNumber: storeToEdit.contactNumber || "",
        storeMapLink: storeToEdit.mapLink || "",
        emailAddress: storeToEdit.email || "",
      };
      console.log("Form data to reset:", formData);

      // Use setTimeout to ensure the form is ready before resetting
      setTimeout(() => {
        form.reset(formData);
        setImagePreview(storeToEdit.storeImageUri || null);
      }, 100);
    }
  }, [storeToEdit, open, form]);

  // Update form resolver when business type changes
  React.useEffect(() => {
    if (isOnlineBusiness) {
      form.clearErrors();
    }
  }, [isOnlineBusiness, form]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (open && storeToEdit) {
      console.log("Modal opened with store data:", storeToEdit);
      const formData = {
        storeName: storeToEdit.storeName || "",
        address: storeToEdit.address || "",
        zipCode: storeToEdit.zipCode || "",
        city: storeToEdit.city || "",
        isEpos: storeToEdit.isEpos || false,
        latitude: parseFloat(storeToEdit.latitude) || 24.4467032,
        longitude: parseFloat(storeToEdit.longitude) || 54.6178367,
        // Keep existing image value so validation passes when no new file is chosen
        storeImage: storeToEdit.storeImageUri || null,
        contactNumber: storeToEdit.contactNumber || "",
        storeMapLink: storeToEdit.mapLink || "",
        emailAddress: storeToEdit.email || "",
      };
      console.log("Setting form data:", formData);
      form.reset(formData);
      setImagePreview(storeToEdit.storeImageUri || null);
    }
  }, [open, storeToEdit, form]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      form.setValue("storeImage", file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (data: any) => {
    if (!storeToEdit) return;

    setIsLoading(true);
    try {
      if (isOnlineBusiness) {
        // Handle online business store update
        const response = await MerchantApi.addMerchantStore({
          ...data,
          id: storeToEdit.id, // Add the store ID for update
          merchantVO: { id: merchantId },
        });
        onUpdateStore(response?.data);
      } else {
        // Handle offline business store update via v2 endpoint
        const formData = new FormData();
        const merchantStoreVO = {
          id: storeToEdit.id, // Add the store ID for update
          merchantVO: { id: Number(merchantId) },
          storeName: data.storeName,
          address: data.address,
          zipCode: data.zipCode,
          city: data.city,
          contactNumber: data.contactNumber,
          mapLink: data.storeMapLink,
          email: data.emailAddress,
        };
        formData.append(
          "merchantStoreVO",
          new Blob([JSON.stringify(merchantStoreVO)], {
            type: "application/json",
          })
        );
        // Append image if a new one was uploaded, otherwise send empty blob
        if (data.storeImage instanceof File) {
          formData.append("merchantStoreImage", data.storeImage);
        }
        const response = await MerchantApi.addOfflineMerchantStore(formData);
        onUpdateStore(response?.data);
      }
      toast.success("Store updated successfully!");
      form.reset();
      setImagePreview(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.message || "Failed to update store");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset loading state when dialog is closed
  React.useEffect(() => {
    if (!open) {
      setIsLoading(false);
    }
  }, [open]);

  if (!storeToEdit) return null;

  // Debug: Log current form values
  console.log("Current form values:", form.getValues());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl scrollbar h-[90%] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            Edit Store {isOnlineBusiness ? "(Online)" : "(Offline)"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {isOnlineBusiness ? (
              // Online Business Form Fields
              <>
                <FormField
                  control={form.control}
                  name="storeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Store Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Downtown Branch"
                          {...field}
                          onChange={(e) => {
                            // Only allow letters, numbers, and spaces
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9\s]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isEpos"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <input
                          type="checkbox"
                          id="epos"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4"
                        />
                      </FormControl>
                      <Label htmlFor="epos" className="!mt-auto">
                        EPOS
                      </Label>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Store address or URL for online stores"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. AB12345"
                          maxLength={10}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^a-zA-Z0-9]/g, "")
                              .slice(0, 10);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Abu Dhabi"
                          {...field}
                          onChange={(e) => {
                            // Only allow letters and spaces
                            const value = e.target.value.replace(
                              /[^a-zA-Z\s]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="latitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latitude</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 24.35168" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="longitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Longitude</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 54.6425625" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              // Offline Business Form Fields
              <>
                {/* Store Image Upload */}
                <FormField
                  control={form.control}
                  name="storeImage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Store Image</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            id="edit-store-image-upload"
                          />
                          <label
                            htmlFor="edit-store-image-upload"
                            className="cursor-pointer block"
                          >
                            <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-gray-400 transition-colors">
                              {imagePreview ? (
                                <div className="relative w-full h-full">
                                  <img
                                    src={imagePreview}
                                    alt="Store preview"
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                                    <Upload className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Plus className="h-12 w-12 text-gray-400 mb-2" />
                                  <p className="text-sm text-gray-500">
                                    Click to upload store image
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    PNG, JPG up to 10MB
                                  </p>
                                </>
                              )}
                            </div>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="storeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Store Name*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Downtown Branch"
                          {...field}
                          onChange={(e) => {
                            // Only allow letters, numbers, and spaces
                            const value = e.target.value.replace(
                              /[^a-zA-Z0-9\s]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Store Address*</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Store address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emailAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. store@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. AB12345"
                          maxLength={10}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^a-zA-Z0-9]/g, "")
                              .slice(0, 10);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Abu Dhabi"
                          {...field}
                          onChange={(e) => {
                            // Only allow letters and spaces
                            const value = e.target.value.replace(
                              /[^a-zA-Z\s]/g,
                              ""
                            );
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact No*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. 971501234567"
                          inputMode="numeric"
                          maxLength={15}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace(/[^0-9]/g, "")
                              .slice(0, 15);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="storeMapLink"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Store Map Link</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. https://maps.google.com/..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <DialogFooter className="sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating Store...
                  </>
                ) : (
                  "Update Store"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
